import { useState, useEffect } from 'react';
import Sidebar from '../../components/feature/Sidebar';
import MobileHeader from '../../components/feature/MobileHeader';
import { apiService } from '../../services/api';

interface Statistics {
  most_frequent: { number: number; frequency: number }[];
  least_frequent: { number: number; frequency: number }[];
  delayed_numbers: { number: number; last_draw: number }[];
  patterns: {
    even_odd_ratio: number;
    sum_average: number;
    repetition_rate: number;
  };
}

const lotteryNames = {
  'mega-sena': 'Mega-Sena',
  'quina': 'Quina',
  'lotofacil': 'Lotofácil',
  'lotomania': 'Lotomania',
  'timemania': 'Timemania',
  'dia-de-sorte': 'Dia de Sorte',
  'super-sete': 'Super Sete',
  'dupla-sena': 'Dupla Sena',
  'federal': 'Loteria Federal'
};

export default function Estatisticas() {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [lotteryType, setLotteryType] = useState('mega-sena');
  const [period, setPeriod] = useState('30d');
  const [analysisType, setAnalysisType] = useState('frequency');
  const [syncing] = useState(false);
  const [syncInfo] = useState<{ imported: number; checked_tickets?: number } | null>(null);

  useEffect(() => {
    loadStatistics();
  }, [lotteryType, period, analysisType]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const response = await apiService.getStatistics({
        lottery_type: lotteryType,
        period: period,
        analysis_type: analysisType
      });
      setStatistics(response);
    } catch (error) {
      const msg = apiService.extractErrorMessage(error);
      alert(msg || 'Falha ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  // Estatísticas não fazem sincronização automática; usam dados do banco

  const renderRanking = (title: string, data: any[], key: string, color: string) => (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
      <h3 className="text-xl font-bold mb-4">{title}</h3>
      <div className="space-y-3">
        {data.slice(0, 10).map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 flex items-center justify-center bg-gradient-to-br ${color} rounded-lg text-sm font-bold`}>
                {index + 1}
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg font-bold">
                {item.number.toString().padStart(2, '0')}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold">{item[key]}</p>
              <p className="text-xs text-white/60">
                {key === 'frequency' ? 'vezes' : key === 'last_draw' ? 'sorteios atrás' : ''}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      <Sidebar />
      <MobileHeader />
      
      <div className="md:ml-72 p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Estatísticas</h1>
            <p className="text-white/60 text-sm">Análise detalhada dos números e padrões das loterias</p>
          </div>
        </div>
        {/* Sem sincronização automática aqui */}

        {/* Filtros */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
          <h3 className="text-lg font-bold mb-4">Filtros</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Loteria</label>
              <select
                value={lotteryType}
                onChange={(e) => setLotteryType(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {Object.entries(lotteryNames).map(([key, name]) => (
                  <option key={key} value={key}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Período</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="30d">Últimos 30 dias</option>
                <option value="90d">Últimos 90 dias</option>
                <option value="1y">Último ano</option>
                <option value="all">Todos os tempos</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Análise</label>
              <select
                value={analysisType}
                onChange={(e) => setAnalysisType(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-black focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                <option value="frequency">Frequência</option>
                <option value="delay">Atraso</option>
                <option value="patterns">Padrões</option>
              </select>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <i className="ri-bar-chart-2-line text-lg"></i>
                </div>
                <h3 className="font-bold">Mais Sorteados</h3>
              </div>
              <p className="text-2xl font-bold text-green-400">
                {statistics.most_frequent[0]?.number?.toString().padStart(2, '0') || '--'}
              </p>
              <p className="text-sm text-white/60">
                {statistics.most_frequent[0]?.frequency || 0} vezes
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-red-500 to-pink-600 rounded-lg">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <h3 className="font-bold">Mais Atrasado</h3>
              </div>
              <p className="text-2xl font-bold text-red-400">
                {statistics.delayed_numbers[0]?.number?.toString().padStart(2, '0') || '--'}
              </p>
              <p className="text-sm text-white/60">
                {statistics.delayed_numbers[0]?.delay || 0} sorteios
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg">
                  <i className="ri-calculator-line text-lg"></i>
                </div>
                <h3 className="font-bold">Soma Média</h3>
              </div>
              <p className="text-2xl font-bold text-blue-400">
                {statistics.patterns?.sum_average?.toFixed(0) || '0'}
              </p>
              <p className="text-sm text-white/60">Média dos sorteios</p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg">
                  <i className="ri-percent-line text-lg"></i>
                </div>
                <h3 className="font-bold">Pares/Ímpares</h3>
              </div>
              <p className="text-2xl font-bold text-purple-400">
                {(statistics.patterns?.even_odd_ratio * 100 || 0).toFixed(0)}%
              </p>
              <p className="text-sm text-white/60">Taxa de pares</p>
            </div>
          </div>
        )}

        {/* Rankings */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-cyan-400 animate-spin mb-4"></i>
              <p className="text-white/60">Carregando estatísticas...</p>
            </div>
          </div>
        ) : statistics ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {(analysisType === 'frequency' || analysisType === 'patterns') && renderRanking(
              'Mais Sorteados',
              statistics.most_frequent,
              'frequency',
              'from-green-500 to-emerald-600'
            )}
            {analysisType === 'frequency' && renderRanking(
              'Menos Sorteados',
              statistics.least_frequent,
              'frequency',
              'from-red-500 to-pink-600'
            )}
            {(analysisType === 'delay') && renderRanking(
              'Mais Atrasados',
              statistics.delayed_numbers,
              'delay',
              'from-orange-500 to-yellow-600'
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <i className="ri-bar-chart-2-line text-6xl text-white/20 mb-4"></i>
            <h3 className="text-xl font-bold mb-2">Nenhuma estatística disponível</h3>
            <p className="text-white/60">Selecione uma loteria e período para visualizar as análises.</p>
          </div>
        )}
      </div>
    </div>
  );
}
